import { useEffect, useState, useCallback, useRef } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { applyRenderStrategy } from '../components/utils/applyRenderStrategy';
import { CREATE_THREAD_MUTATION } from '../mutations/createThreadMutation';
import { SUBMIT_USER_INPUT_MUTATION } from '../mutations/submitUserInputMutation';
import { buildThreadIdentity } from '../utils/buildThreadIdentity';

/**
 * Hook for inline conversation management (federated components)
 *
 * Combines thread creation and message sending without navigation.
 * Perfect for self-contained conversational components like VeloQA.
 *
 * @param {object} props - Configuration object
 * @param {string} props.tag - Tag identifier for thread context. Pattern: DOMAIN.namespace.key (e.g., COMPLIANCE.businessLicenses.blfPurchased)
 * @param {string} props.origination - Where the thread was initiated (e.g., zendash-sidebar)
 * @param {string} props.sourceComponent - Component that created the thread (e.g., velo-qa)
 * @param {object} props.initialState - Initial state of the conversation { threadUuid, sourceComponent }
 * @param {Function} props.onStateChange - Callback when state changes
 * @param {Function} props.onError - Callback when an error occurs
 * @returns {object} Hook state and methods
 */
export function useInlineConversation({
  tag,
  origination,
  sourceComponent,
  initialState,
  onStateChange,
  onError
}) {
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'inlineConversation'
    }
  });

  const { threadUuid: initialThreadUuid } = initialState || {};

  // State
  const [threadUuid, setThreadUuid] = useState(initialThreadUuid || null);
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasResetOnInit, setHasResetOnInit] = useState(false);

  useEffect(() => {
    if (!error) return;
    onError?.({ type: 'thread_creation_failed', reason: error.message });
  }, [error, onError]);

  // Refs
  const isCreatingThread = useRef(false);

  // Mutations
  const [createThread] = useMutation(CREATE_THREAD_MUTATION);
  const [submitUserInput] = useMutation(SUBMIT_USER_INPUT_MUTATION);

  /**
   * Process and add new instructions
   */
  const processInstructions = useCallback((currentInstructions, newInstructionData) => {
    let updatedInstructions = [...currentInstructions];

    newInstructionData.forEach((instruction) => {
      updatedInstructions = applyRenderStrategy(updatedInstructions, instruction);
    });

    return updatedInstructions;
  }, []);

  /**
   * Reset conversation
   */
  const reset = useCallback(() => {
    setThreadUuid(null);
    setInstructions([]);
    setLoading(false);
    setError(null);
    isCreatingThread.current = false;
    onStateChange?.({ threadUuid: null, sourceComponent });
  }, [onStateChange, sourceComponent]);

  /**
   * Create a new thread
   */
  const createNewThread = useCallback(
    async (message, messageType, mode, { visitUuid, userUuid, businessEntityUuid }) => {
      if (isCreatingThread.current) {
        return null;
      }

      isCreatingThread.current = true;
      setError(null);

      try {
        const { identity, threadMode } = buildThreadIdentity(
          mode,
          visitUuid,
          userUuid,
          businessEntityUuid
        );

        const input = {
          businessEntityUuid,
          messageType,
          message,
          role: 'user',
          tag,
          origination,
          sourceComponent
        };

        const result = await createThread({
          variables: {
            identity,
            input,
            threadMode
          }
        });

        const threadData = result?.data?.createThread;
        const newThreadUuid = threadData?.threadUuid;
        const instructionData = threadData?.data || [];

        if (threadData?.error) {
          const responseError = new Error(threadData.error.message);
          responseError.code = threadData.error.code;
          setError(responseError);
          throw responseError;
        }

        if (!newThreadUuid) {
          const err = new Error('Thread creation failed: No thread UUID returned');
          setError(err);
          throw err;
        }

        // Store thread UUID
        setThreadUuid(newThreadUuid);

        // Process and store instructions
        const newInstructions = processInstructions([], instructionData);
        setInstructions(newInstructions);
        onStateChange?.({ threadUuid: newThreadUuid, sourceComponent });

        return { threadUuid: newThreadUuid, instructions: newInstructions };
      } catch (err) {
        captureException(err, {
          tags: {
            errorType: 'thread_creation_failed',
            userMode: mode
          }
        });
        setError(err);
        throw err;
      } finally {
        isCreatingThread.current = false;
      }
    },
    [
      createThread,
      processInstructions,
      captureException,
      tag,
      origination,
      sourceComponent,
      onStateChange
    ]
  );

  /**
   * Send message to existing thread
   */
  const sendMessage = useCallback(
    async (message, messageType, mode, { visitUuid, userUuid, businessEntityUuid }) => {
      if (!threadUuid) {
        throw new Error('No thread UUID available. Thread must be created first.');
      }

      setError(null);

      try {
        const { identity } = buildThreadIdentity(mode, visitUuid, userUuid, businessEntityUuid);

        const inputObject = {
          businessEntityUuid,
          messageType,
          message,
          role: 'user',
          tag,
          origination,
          sourceComponent
        };

        // Get last message UUID for threading
        const lastMessageUuid =
          instructions.length > 0 ? instructions[instructions.length - 1].id : null;

        let variables = {
          identity,
          input: inputObject,
          threadUuid,
          messageUuid: lastMessageUuid,
          mode: 'post'
        };

        // Perform GET if thread supplied in the initial state
        if (initialThreadUuid) {
          variables = {
            identity,
            threadUuid,
            mode: 'get',
            threadMode: mode
          };
        }

        const response = await submitUserInput({ variables });

        const responseData = response?.data?.interactWithAssistant;
        const newInstructionData = responseData?.data || [];
        const isSingleResponseThread =
          newInstructionData.filter((m) => m.attributes.role === 'assistant').length === 1;

        // Fallback to empty state if showing a thread on init and thread is not single response
        if (!!initialThreadUuid && !isSingleResponseThread) {
          reset();
          setHasResetOnInit(true);
          return { instructions };
        }

        if (responseData && (!responseData.ok || responseData.error)) {
          const serverError = new Error(responseData.error?.message || 'Server error in response');
          const threadNotFound = responseData?.error?.errorType === 'INVALID_THREAD';

          // Fallback to empty state if thread is not found
          if (threadNotFound) {
            reset();
            setHasResetOnInit(true);
            return { instructions };
          }

          setError(serverError);
          throw serverError;
        }

        // Process and update instructions
        const updatedInstructions = processInstructions(instructions, newInstructionData);
        setInstructions(updatedInstructions);
        onStateChange?.({ threadUuid, sourceComponent });
        return { instructions: updatedInstructions };
      } catch (err) {
        captureException(err, {
          tags: {
            errorType: 'message_send_failed',
            threadUuid,
            userMode: mode
          }
        });
        setError(err);
        throw err;
      }
    },
    [
      reset,
      threadUuid,
      instructions,
      submitUserInput,
      processInstructions,
      captureException,
      onStateChange,
      initialThreadUuid,
      tag,
      origination,
      sourceComponent
    ]
  );

  /**
   * Send a message (creates thread if needed)
   */
  const sendMessageOrCreateThread = useCallback(
    async (message, messageType, mode, identityParams) => {
      setLoading(true);

      try {
        if (threadUuid) {
          // Thread exists, send message
          return await sendMessage(message, messageType, mode, identityParams);
        }
        // No thread, create one
        return await createNewThread(message, messageType, mode, identityParams);
      } finally {
        setLoading(false);
      }
    },
    [threadUuid, sendMessage, createNewThread]
  );

  /**
   * Add optimistic user response
   */
  const addUserResponse = useCallback((message) => {
    const optimisticInstruction = {
      type: 'UserResponse',
      content: message,
      role: 'user',
      id: `optimistic-${Date.now()}`
    };
    setInstructions((prev) => [...prev, optimisticInstruction]);
  }, []);

  return {
    // State
    threadUuid,
    instructions,
    loading,
    error,
    hasResetOnInit,

    // Actions
    sendMessage: sendMessageOrCreateThread,
    addUserResponse,
    reset,

    // Computed
    hasThread: Boolean(threadUuid),
    hasInstructions: instructions.length > 0
  };
}
